import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Palette } from 'lucide-react';
import { useTheme, THEMES, type ThemeId } from '@/theme/ThemeContext';

// Swatches mirror each theme's --primary accent color (not the pale
// sidebar/page tint) so the dot people click on actually matches the
// most visible color the theme applies (active nav pill, badges, buttons).
// Exception: "light" itself has no strong accent hue as its defining
// trait (its sidebar/canvas are neutral grey/white) — showing its blue
// button accent as the swatch reads as "this is a blue theme", which is
// wrong, so it gets a neutral grey swatch instead.
const SWATCHES: Record<ThemeId, string> = {
  light: '#D1D5DB',
  'slate-dark': '#58A6FF',
  'ocean-blue': '#3B82F6',
  'forest-green': '#15803D',
  'midnight-violet': '#8B5CF6',
  lavender: '#9B8AC4',
  blue: '#0EA5E9',
  'soft-grey': '#6B7280',
};

const LABEL_KEYS: Record<ThemeId, string> = {
  light: 'theme.light',
  'slate-dark': 'theme.slateDark',
  'ocean-blue': 'theme.oceanBlue',
  'forest-green': 'theme.forestGreen',
  'midnight-violet': 'theme.midnightViolet',
  lavender: 'theme.lavender',
  blue: 'theme.blue',
  'soft-grey': 'theme.softGrey',
};

interface Props {
  className?: string;
}

export function ThemeSelector({ className = '' }: Props) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(id: ThemeId) {
    setTheme(id);
    setOpen(false);
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(o => !o)}
        title={t('theme.select')}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition"
      >
        <Palette size={15} />
        <span
          className="w-3 h-3 rounded-full border border-black/10"
          style={{ backgroundColor: SWATCHES[theme] }}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-chrome-page border border-gray-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden">
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            {t('theme.select')}
          </p>
          {THEMES.map(id => (
            <button
              key={id}
              onClick={() => select(id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition
                ${theme === id
                  ? 'bg-gray-50 font-medium text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              <span
                className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0"
                style={{ backgroundColor: SWATCHES[id] }}
                aria-hidden="true"
              />
              <span>{t(LABEL_KEYS[id])}</span>
              {theme === id && <span className="ml-auto text-primary-600">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
